'use client';

import { GaugeComponent } from 'react-gauge-component';

import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from 'recharts';

import { type CertikDataSchemaPayload } from '@api/token/coinInfo';

import { Chip, ChipSize, ChipVariant } from '@ui/uikit/components/common/Chip';
import { Hint, HintVariant } from '@ui/uikit/components/common/Hint';
import { Danger } from '@ui/uikit/components/icons/mono/Danger';
import { ExclamationPoint } from '@ui/uikit/components/icons/mono/ExclamationPoint';
import { MarkInfo } from '@ui/uikit/components/icons/mono/MarkInfo';
import { Shield } from '@ui/uikit/components/icons/mono/Shield';
import { Certik } from '@ui/uikit/components/icons/poly/Certik';
import { cn } from '@ui/uikit/utils';
import { Link } from '@utilities/Link';

import { CertikHighlightTooltip } from './CertikHighlightTooltip';
import styles from './SkynetScoreChart.module.scss';

const getScoreColor = (tier?: string): string => {
  if (tier?.includes('B')) {
    return 'var(--border-color-orange)';
  }

  if (tier?.includes('C')) {
    return 'var(--border-color-red)';
  }

  return 'var(--border-color-green)';
};

export const getScoreChipVariant = (tier?: string): ChipVariant => {
  if (tier?.includes('B')) {
    return ChipVariant.ORANGE;
  }

  if (tier?.includes('C')) {
    return ChipVariant.RED;
  }

  return ChipVariant.GREEN;
};

interface SkynetScoreChartProps {
  certikData?: CertikDataSchemaPayload;
  tokenSymbol: string;
}

const SkynetScoreChart = ({ certikData, tokenSymbol }: SkynetScoreChartProps) => {
  const scoreObj = certikData?.securityScore;
  const certikLink = certikData?.certiKProjectPage;

  const value = scoreObj?.score ?? 0;
  const [integer, decimal] = value.toFixed(2).split('.');

  const radarChartData = [
    {
      subject: `Code\n${scoreObj?.codeSecurity ?? '—'}`,
      A: scoreObj?.codeSecurity ?? 0,
    },
    {
      subject: `Operational\n${scoreObj?.operationalResilience ?? '—'}`,
      A: scoreObj?.operationalResilience ?? 0,
    },
    {
      subject: `Market\n${scoreObj?.marketStability ?? '—'}`,
      A: scoreObj?.marketStability ?? 0,
    },
    {
      subject: `Community\n${scoreObj?.communityTrust ?? '—'}`,
      A: scoreObj?.communityTrust ?? 0,
    },
    {
      subject: `Governance\n${scoreObj?.governanceStrength ?? '—'}`,
      A: scoreObj?.governanceStrength ?? 0,
    },
    {
      subject: `Fundamental\n${scoreObj?.fundamentalHealth ?? '—'}`,
      A: scoreObj?.fundamentalHealth ?? 0,
    },
  ];

  const tier = scoreObj?.tier;
  const color = getScoreColor(tier);
  const softColor = color.replace(/\)$/, '-soft)');
  const chipVariant = getScoreChipVariant(tier);

  return (
    <div className={styles['sectionWrapper']}>
      <div className={styles['chartSection']}>
        <div className={styles['chartWrapper']}>
          <div className={styles['chart']}>
            <div className={styles['analytics']}>
              <div>
                <div className={styles['label']}>Rank</div>
                <div className={styles['value']}>#{scoreObj?.rank ?? '—'}</div>
              </div>
              <div>
                <div className={styles['label']}>Percentile</div>
                <div className={styles['value']}>
                  {scoreObj?.rankPercentile != null ? `${scoreObj.rankPercentile}%` : '—'}
                </div>
              </div>
            </div>

            <div className={styles['gaugeWrapper']}>
              <GaugeComponent
                type="semicircle"
                style={{ width: '260px', height: '130px' }}
                pointer={{ hide: true }}
                marginInPercent={{ top: 0.05, bottom: 0, left: 0.07, right: 0.07 }}
                value={value}
                arc={{
                  width: 0.08,
                  padding: 0,
                  cornerRadius: 0,
                  subArcs: [
                    { limit: value, color: color },
                    { limit: 100, color: 'var(--border-color-medium)' },
                  ],
                }}
                labels={{
                  valueLabel: { hide: true },
                  tickLabels: {
                    type: 'inner',
                    ticks: [
                      {
                        value: 0,
                        lineConfig: { hide: true },
                        valueConfig: {
                          formatTextValue: (value) => `${value}`,
                          style: {
                            fontSize: 11,
                            fill: 'var(--text-color-quaternary)',
                            transform: 'translate(0, -5px)',
                          },
                        },
                      },
                      {
                        value: 100,
                        lineConfig: { hide: true },
                        valueConfig: {
                          formatTextValue: (value) => `${value}`,
                          style: {
                            fontSize: 11,
                            fill: 'var(--text-color-quaternary)',
                            transform: 'translate(0, -5px)',
                          },
                        },
                      },
                    ],
                  },
                }}
              />

              <div className={styles['badge']}>
                <Chip variant={chipVariant} size={ChipSize.SMALL} isReversed text={tier ?? 'N/A'} />
              </div>
              <div className={styles['gaugeValue']}>
                <span className={styles['integer']}>{integer}</span>
                <span className={styles['decimal']}>.{decimal}</span>
              </div>
              <div className={styles['statusGrid']}>
                <CertikHighlightTooltip
                  title="Highlights"
                  items={certikData?.highlights}
                  Icon={<Shield color="var(--border-color-green)" />}
                >
                  <Hint
                    message={certikData?.highlights?.length}
                    variant={
                      certikData?.highlights?.length ? HintVariant.SUCCESS : HintVariant.DISABLED
                    }
                    Icon={Shield}
                    bold
                  />
                </CertikHighlightTooltip>
                <CertikHighlightTooltip
                  title="Information"
                  items={[]}
                  Icon={<MarkInfo color="var(--border-color-blue)" />}
                >
                  <Hint
                    message={certikData?.alerts?.length}
                    variant={certikData?.alerts?.length ? HintVariant.DANGER : HintVariant.DISABLED}
                    bold
                    Icon={MarkInfo}
                  />
                </CertikHighlightTooltip>
                <CertikHighlightTooltip
                  title="Warning"
                  items={[]}
                  Icon={<ExclamationPoint color="var(--border-color-orange)" />}
                >
                  <Hint
                    message={certikData?.alerts?.length}
                    variant={certikData?.alerts?.length ? HintVariant.DANGER : HintVariant.DISABLED}
                    Icon={ExclamationPoint}
                    bold
                  />
                </CertikHighlightTooltip>
                <CertikHighlightTooltip
                  title="Danger"
                  items={certikData?.alerts}
                  Icon={<Danger color="var(--border-color-red)" />}
                >
                  <Hint
                    message={certikData?.alerts?.length}
                    variant={certikData?.alerts?.length ? HintVariant.DANGER : HintVariant.DISABLED}
                    Icon={Danger}
                    bold
                  />
                </CertikHighlightTooltip>
              </div>
            </div>

            <div className={styles['info']} />
          </div>

          <div className={cn(styles['footer'], styles['label'])}>
            <span>{`${tokenSymbol} on`}</span>
            <Certik width="16px" />
            {certikLink ? (
              <Link href={certikLink} target="_blank" className={styles['footerBrand']}>
                CertiK Skynet
              </Link>
            ) : (
              <span className={styles['footerBrand']}>CertiK Skynet</span>
            )}
          </div>
        </div>
      </div>
      <div className={styles['chartSection']}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart
            width={244}
            height={244}
            cx="50%"
            cy="47%"
            outerRadius={78}
            data={radarChartData}
          >
            <PolarGrid stroke="var(--border-color-medium)" strokeWidth={1} />
            <PolarAngleAxis
              dataKey="subject"
              tick={(props) => {
                const { x, y, cx, cy, payload } = props;
                const [label, value] = payload.value.split('\n');

                const dx = x - cx;
                const dy = y - cy;

                const factor = 1.25;
                const newX = cx + dx * factor;
                const newY = cy + dy * factor;

                return (
                  <text
                    x={newX}
                    y={newY}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={11}
                  >
                    <tspan
                      x={newX}
                      dy="-0.2em"
                      className={styles['radialLabel']}
                      fill="var(--text-color-tertiary)"
                    >
                      {label}
                    </tspan>
                    <tspan
                      x={newX}
                      dy="1.2em"
                      className={styles['radialValue']}
                      fill="var(--text-color-primary)"
                    >
                      {value}
                    </tspan>
                  </text>
                );
              }}
            />
            <PolarRadiusAxis tick={false} axisLine={false} domain={[0, 100]} />
            <Radar
              name="Skynet Score RadarChart"
              dataKey="A"
              stroke={color}
              fill={softColor}
              fillOpacity={0.08}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export { SkynetScoreChart };
